import React, { useState, useEffect } from 'react';
import { Icon, Text, Button, Modal } from '@/ui-kits';
import { getProviders, connectProvider, disconnectProvider } from '@/services/api';
import { useToast } from '@/hooks/useToast';

export const SpecialistSettings: React.FC = () => {
  const [googleConnected, setGoogleConnected] = useState(false);
  const [teamsConnected, setTeamsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [providerToDisconnect, setProviderToDisconnect] = useState<number | null>(null);
  const { showToast } = useToast();

  const fetchProviders = React.useCallback(async () => {
    try {
      const response = await getProviders();
      const providers = response?.data?.data || [];
      const google = providers.find((p: any) => p.provider === 1);
      const teams = providers.find((p: any) => p.provider === 2);

      setGoogleConnected(google?.is_connected || false);
      setTeamsConnected(teams?.is_connected || false);
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'OAUTH_COMPLETE') {
        if (event.data.status === 'connected') {
          fetchProviders();
          showToast(`Successfully connected to ${event.data.provider}`, 'success');
        } else {
          console.error("OAuth failed:", decodeURIComponent(event.data.message));
          showToast(`Connection failed: ${decodeURIComponent(event.data.message)}`, 'failure');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [fetchProviders]);

  const handleConnect = async (provider: number) => {
    try {
      const response = await connectProvider(provider);
      const authUrl = response?.data?.data?.auth_url;
      if (authUrl) {
        window.open(authUrl, 'oauth', 'width=500,height=600');
      }
    } catch (error) {
      console.error(`Error connecting provider ${provider}:`, error);
    }
  };

  const confirmDisconnect = (provider: number) => {
    setProviderToDisconnect(provider);
    setDisconnectModalOpen(true);
  };

  const onConfirmDisconnect = async () => {
    if (providerToDisconnect !== null) {
      setLoading(true);
      try {
        await disconnectProvider(providerToDisconnect);
        await fetchProviders();
      } catch (error) {
        console.error(`Error disconnecting provider ${providerToDisconnect}:`, error);
        setLoading(false);
      }
      setDisconnectModalOpen(false);
      setProviderToDisconnect(null);
    }
  };

  return (
    <div className="flex flex-col grow p-3 sm:p-4 md:p-6 lg:p-8 bg-[#F9FAFB] h-full">
      <div className="flex flex-col gap-4 sm:gap-6 mt-0 sm:mt-2 md:mt-4 max-w-6xl mx-auto w-full">
        {/* Video Consultation Provider Card */}
        <div className="bg-white border border-ui_input_border rounded-2xl p-4 sm:p-4 md:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2 sm:gap-0">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-[#CBEBFB66] rounded-lg flex-shrink-0">
                <Icon type="consultation" className="size-7 sm:size-7 md:size-8" />
              </div>
              <div>
                <Text variant="heading" className="text-deep_blue !font-FigtreeSemiBold">
                  Video Consultation Providers
                </Text>
                <Text variant="body" className="text-deep_blue !font-FigtreeRegular text-sm">
                  Connect your preferred meeting account (Only one active provider allowed)
                </Text>
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-6 flex flex-col gap-4">

            {/* Google Meet Provider */}
            <div className="p-5 rounded-[24px] border border-ui_input_border bg-[#F8FCF7] shadow-sm flex flex-col justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-3xl bg-white shadow-[0_10px_30px_rgba(15,157,88,0.12)]">
                  <Icon type="google_meet" className="size-12" />
                </div>
                <div className="flex-1">
                  <Text variant="subtitle" className="font-FigtreeSemiBold text-deep_blue">Google Meet</Text>
                  <div className="mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium border border-ui_input_border bg-white">
                    <span className={googleConnected ? 'h-2.5 w-2.5 rounded-full bg-[#22C55E]' : 'h-2.5 w-2.5 rounded-full bg-ui_placeholder'} />
                    <span className={googleConnected ? 'text-[#047857]' : 'text-ui_placeholder'}>
                      {loading ? 'Loading...' : googleConnected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                title={googleConnected ? 'Disconnect' : 'Connect'}
                variant={googleConnected ? 'secondary' : 'primary'}
                onClick={() => googleConnected ? confirmDisconnect(1) : handleConnect(1)}
                className="w-full sm:w-auto"
                disabled={loading}
              />
            </div>

            {/* Microsoft Teams Provider */}
            <div className="p-5 rounded-[24px] border border-ui_input_border bg-[#EFF1FF] shadow-sm flex flex-col justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-3xl bg-white shadow-[0_10px_30px_rgba(70,71,117,0.12)]">
                  <Icon type="microsoft_teams" className="size-12" />
                </div>
                <div className="flex-1">
                  <Text variant="subtitle" className="font-FigtreeSemiBold text-deep_blue">Microsoft Teams</Text>
                  <div className="mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium border border-ui_input_border bg-white">
                    <span className={teamsConnected ? 'h-2.5 w-2.5 rounded-full bg-[#22C55E]' : 'h-2.5 w-2.5 rounded-full bg-ui_placeholder'} />
                    <span className={teamsConnected ? 'text-[#047857]' : 'text-ui_placeholder'}>
                      {loading ? 'Loading...' : teamsConnected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                title={teamsConnected ? 'Disconnect' : 'Connect'}
                variant={teamsConnected ? 'secondary' : 'primary'}
                onClick={() => teamsConnected ? confirmDisconnect(2) : handleConnect(2)}
                className="w-full sm:w-auto"
                disabled={loading}
              />
            </div>

          </div>
        </div>
      </div>

      {/* Disconnect Confirmation Modal */}
      <Modal open={disconnectModalOpen} onClose={() => setDisconnectModalOpen(false)} className="rounded-2xl max-w-md w-full p-6">
        <div className="flex flex-col items-center text-center gap-4">
          <div className="p-3 bg-red-50 rounded-full mb-2">
            <Icon type="warning" className="size-10 object-contain" />
          </div>
          <div>
            <Text variant="title" className="text-deep_blue !font-FigtreeBold">
              Disconnect Provider?
            </Text>
            <Text variant="body" className="text-ui_placeholder mt-3 text-sm">
              Are you sure you want to disconnect this video consultation provider? You will need to re-authenticate to connect it again.
            </Text>
          </div>
          <div className="flex w-full gap-3 mt-4">
            <Button
              title="Cancel"
              variant="secondary"
              onClick={() => setDisconnectModalOpen(false)}
              className="flex-1"
            />
            <Button
              title="Disconnect"
              variant="primary"
              className="flex-1 !bg-rejection border-none hover:opacity-90"
              onClick={onConfirmDisconnect}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};
